import Service from 'react-reservice';

import notificationAPI, { ENotificationType, INotification } from '@webtrader/core/api/notifications';
import revertNotificationAPI from '@webtrader/core/api/notifications/revert';
import { ENotificationSettingsType } from '@webtrader/core/api/notifications/settings';
import notificationSummaryAPI, { INotificationSummary } from '@webtrader/core/api/notifications/summary';
import { configService } from '@webtrader/core/services/config';
import { EAppType } from '@webtrader/core/services/config/service';

export interface IPushNotificationData {
  eventId?: string;
  orderId?: number | null;
  metatraderId?: number;
  serverId?: number;
  settingType: ENotificationSettingsType;
  symbol?: string;
  eventDate?: number;
  deviceIos?: string;
  deviceAndroid?: string;
  deviceIosToKick?: string;
  deviceAndroidToKick?: string;
  activeIosDevice?: string;
  activeAndroidDevice?: string;
}

interface IOrderNotification {
  metatraderId: number;
  orderId: number;
  settingType: ENotificationSettingsType;
}

interface INotificationsContext {
  initial: boolean;
  messages: INotification[];
  summaries: INotificationSummary[];
  loadingMessages: boolean;
  pushedNotification?: IPushNotificationData;
  notificationSettingsType?: ENotificationSettingsType;
  selectedNotificationType: ENotificationType;
  orderNotification?: IOrderNotification;
  unreadOrderNotifications: number;
  unreadSystemNotifications: number;
  unreadInformationNotifications: number;
  centroidOrderNotification: INotification | undefined;
}

const defaultContext: INotificationsContext = {
  initial: true,
  messages: [],
  summaries: [],
  loadingMessages: true,
  selectedNotificationType: ENotificationType.ORDER,
  unreadOrderNotifications: 0,
  unreadSystemNotifications: 0,
  unreadInformationNotifications: 0,
  centroidOrderNotification: undefined
};

class NotificationsService extends Service<INotificationsContext> {
  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public init = () => {
    this.fetchNotificationSummary();
  };

  public reset = () => {
    this.updateSummaries();

    const { summaries } = this.context;

    this.context = {
      ...defaultContext,
      summaries
    };
  };

  // This API is meant for development testing only
  public revert = () => {
    revertNotificationAPI();
  };

  public setNotInitial = () => {
    this.context.initial = false;
  };

  public updateSummaries = () => {
    this.context.summaries = this.context.summaries.map((s) => {
      if (s.notificationType === this.context.selectedNotificationType) {
        return {
          ...s,
          unread: 0
        };
      }

      return s;
    });
  };

  public onTabChanged = () => {
    this.context.messages = [];
    this.context.loadingMessages = true;
  };

  public onTabChange = (nextType: string) => {
    const {
      selectedNotificationType,
      unreadOrderNotifications,
      unreadSystemNotifications,
      unreadInformationNotifications
    } = this.context;

    if (nextType in ENotificationType) {
      const nextNotificationType = nextType as ENotificationType;

      this.context.messages = [];

      this.fetchNotificationsByType(nextNotificationType);

      if (selectedNotificationType === ENotificationType.ORDER && unreadOrderNotifications > 0) {
        this.resetUnreadOrderNotifications();
      } else if (selectedNotificationType === ENotificationType.SYSTEM && unreadSystemNotifications > 0) {
        this.resetUnreadSystemNotifications();
      } else if (selectedNotificationType === ENotificationType.INFORMATION && unreadInformationNotifications > 0) {
        this.resetUnreadInformationNotifications();
      }

      this.updateSummaries();

      this.context.selectedNotificationType = nextNotificationType;
    }
  };

  public fetchNotifications = async (loading = false) => {
    await this.fetchNotificationsByType(this.context.selectedNotificationType, loading);
  };

  public fetchNotificationsByType = async (notificationType: ENotificationType, loading = true) => {
    const appType = configService.getAppType();

    try {
      this.context.loadingMessages = appType === EAppType.PC ? true : loading;

      const { result } = await notificationAPI({
        notificationType
      });

      this.context.messages = result || [];

      switch (notificationType) {
        case ENotificationType.ORDER:
          this.context.unreadOrderNotifications = 0;
          break;
        case ENotificationType.SYSTEM:
          this.context.unreadSystemNotifications = 0;
          break;
        case ENotificationType.INFORMATION:
          this.context.unreadInformationNotifications = 0;
          break;
        default:
          break;
      }
    } catch {
    } finally {
      this.context.loadingMessages = false;
    }
  };

  public fetchNotificationSummary = async () => {
    const appType = configService.getAppType();
    try {
      const { result } = await notificationSummaryAPI();

      this.context.summaries = result ?? [];

      if (result && result.length > 0) {
        result.forEach((notificationSummary) => {
          const { notificationType, unread } = notificationSummary;

          this.setUnreadNotifications(notificationType, unread);
        });
      } else if (appType === EAppType.MOBILE) {
        this.resetUnreadOrderNotifications();
        this.resetUnreadSystemNotifications();
        this.resetUnreadInformationNotifications();
      }
    } catch {}
  };

  public resetUnreadOrderNotifications = () => {
    this.context.unreadOrderNotifications = 0;
  };

  public resetUnreadSystemNotifications = () => {
    this.context.unreadSystemNotifications = 0;
  };

  public resetUnreadInformationNotifications = () => {
    this.context.unreadInformationNotifications = 0;
  };

  public setOrderNotification = (order: IOrderNotification) => {
    this.context.orderNotification = order;
  };

  public resetOrderNotification = () => {
    this.context.orderNotification = undefined;
  };

  public setCentroidOrderNotification = (notification: INotification) => {
    this.context.centroidOrderNotification = notification;
  };

  public resetCentroidOrderNotification = () => {
    this.context.centroidOrderNotification = undefined;
  };

  public setNotificationType = (notificationType: ENotificationType) => {
    this.context.selectedNotificationType = notificationType;
  };

  public setNotificationSettingType = (notificationSettingsType: ENotificationSettingsType) => {
    this.context.notificationSettingsType = notificationSettingsType;
  };

  public resetNotificationSettingType = () => {
    this.context.notificationSettingsType = undefined;
  };

  public setPushNotificationData = (pushedNotification: IPushNotificationData) => {
    this.context.pushedNotification = pushedNotification;
  };

  public resetPushNotificationData = () => {
    this.context.pushedNotification = undefined;
  };

  private setUnreadNotifications = (notificationType: ENotificationType, unread: number) => {
    switch (notificationType) {
      case ENotificationType.ORDER:
        this.context.unreadOrderNotifications = unread;
        return;
      case ENotificationType.SYSTEM:
        this.context.unreadSystemNotifications = unread;
        return;
      case ENotificationType.INFORMATION:
        this.context.unreadInformationNotifications = unread;
        return;
      default:
        return;
    }
  };
}

export default new NotificationsService();
